import { useEffect } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.ts';
import { useSocketStore } from '../store/socket.ts';
import { notificationsApi, type NotifRow } from '../features/notifications/api.ts';
import { AppSwitcher } from './AppSwitcher.tsx';
import { Icon } from './Icon.tsx';
import { dv } from '../i18n/dv.ts';
import { BODIES, schedulerRoles } from '../lib/body.ts';

type SideItem = {
  to: string;
  label: string;
  icon: string;
  matchPrefix?: string;
  showUnread?: boolean;
};

// Google-Drive-style navigation drawer (desktop): an elevated "new meeting"
// button, then full-width destination rows (icon + label) with a tonal pill
// highlight on the active row. Sits under the top app bar (top-16). Mirrors
// WapSign's Sidebar; RTL so it hugs the inline-start (right) edge. Also owns the
// realtime notifications socket (always mounted while signed in) so the unread
// badge here and in the bottom nav stay live.
export function Sidebar(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { data: notifs } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
    enabled: !!user,
  });
  const unread = notifs?.items.filter((i) => !i.isRead).length ?? 0;

  const connect = useSocketStore((s) => s.connect);
  const socket = useSocketStore((s) => s.socket);
  useEffect(() => { if (user) connect(); }, [user, connect]);
  useEffect(() => {
    if (!socket) return;
    function onNotif(n: NotifRow): void {
      qc.setQueryData<{ items: NotifRow[] } | undefined>(['notifications'], (prev) => ({
        items: prev ? [n, ...prev.items.filter((i) => i.id !== n.id)] : [n],
      }));
    }
    socket.on('notification', onNotif);
    return () => { socket.off('notification', onNotif); };
  }, [socket, qc]);

  if (!user) return null;

  const items: SideItem[] = user.role === 'SYS_ADMIN'
    ? [
        { to: '/admin/orgs', label: dv.nav.orgs, icon: 'apartment', matchPrefix: '/admin' },
        { to: '/notifications', label: dv.nav.notifications, icon: 'notifications', showUnread: true },
        { to: '/profile', label: dv.nav.profile, icon: 'person' },
      ]
    : [
        { to: '/', label: dv.nav.dashboard, icon: 'dashboard' },
        { to: '/notifications', label: dv.nav.notifications, icon: 'notifications', showUnread: true },
        { to: '/meetings', label: dv.nav.meetings, icon: 'event' },
        { to: '/statistics', label: dv.nav.statistics, icon: 'bar_chart', matchPrefix: '/statistics' },
        { to: '/thanfeezu', label: dv.nav.thanfeezu, icon: 'fact_check', matchPrefix: '/thanfeezu' },
        ...(user.isOrgAdmin ? [{ to: '/settings', label: dv.nav.settings, icon: 'settings', matchPrefix: '/settings' }] : []),
        { to: '/profile', label: dv.nav.profile, icon: 'person' },
      ];

  // Same role gate as the Meetings page create button (president / SG / bureau).
  const canSchedule = user.role !== 'SYS_ADMIN' && BODIES.some((b) => schedulerRoles(b).includes(user.role));

  const isActive = (item: SideItem): boolean =>
    item.matchPrefix ? pathname.startsWith(item.matchPrefix) : pathname === item.to;

  const rowBase = 'flex items-center gap-4 rounded-full px-4 py-2.5 text-[0.95rem] transition-colors';

  return (
    <aside
      aria-label={dv.nav.dashboard}
      className="hidden md:flex fixed start-0 top-16 bottom-0 z-30 w-64 flex-col gap-1 px-3 py-4 bg-[hsl(var(--chrome))] overflow-y-auto"
    >
      {canSchedule && (
        <Link
          to="/meetings"
          className="mb-3 inline-flex w-fit items-center gap-3 rounded-2xl bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))] py-3.5 ps-4 pe-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <Icon name="add" size={24} />
          <span className="text-[0.95rem] font-medium">{dv.meetings.create}</span>
        </Link>
      )}

      <nav className="flex flex-col gap-0.5">
        {items.map((item) => {
          const active = isActive(item);
          const showBadge = item.showUnread && unread > 0;
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={[
                rowBase,
                active
                  ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] font-medium'
                  : 'text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]',
              ].join(' ')}
            >
              <Icon name={item.icon} size={22} fill={active} className={active ? '' : 'text-muted-foreground'} />
              <span className="flex-1 truncate">{item.label}</span>
              {showBadge && (
                <span
                  aria-label={`${unread} ${dv.notifications.title}`}
                  className="grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold text-destructive-foreground tabular"
                >
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Ecosystem jump */}
      <div className="mt-auto flex flex-col gap-0.5 pt-2">
        <AppSwitcher variant="sidebar" />
        <a href="https://office.wapplify.app" className={`${rowBase} text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]`}>
          <span className="grid size-[22px] shrink-0 place-items-center">
            <svg width="20" height="20" aria-hidden="true" viewBox="0 0 48 48">
              <rect width="48" height="48" rx="13.5" fill="#006A60" />
              <path d="M9 15.5 17.5 33 24 21.5" fill="none" stroke="#9EF2E4" strokeWidth={5.6} strokeLinecap="round" strokeLinejoin="round" />
              <path d="M24 21.5 30.5 33 39 15.5" fill="none" stroke="#fff" strokeWidth={5.6} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="flex-1 truncate">ވެޕްލިފައި އޮފީސް</span>
          <Icon name="open_in_new" size={16} className="text-muted-foreground" />
        </a>
      </div>
    </aside>
  );
}
